import { OrderTypes } from '@codezo/graphql';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fitToViewport, type RowEstimate } from './fitToViewport';
import { readPagingMode, writePagingMode, type PagingMode } from './pagingMode';
import { useDebouncedValue } from './useDebouncedValue';
import { useQueryParam, useQueryParamInt } from './useQueryParam';

/**
 * Most pages a scroll-mode list asks for in one request when it is rebuilt
 * (back navigation, refetch after a mutation). Past this it rebuilds the first
 * ten and carries on loading from there as you scroll, rather than asking the
 * API for an unbounded page.
 */
const MAX_RESTORE_PAGES = 10;

interface UseListParamsOptions {
	/** Default sort field. Default: 'createdAt' */
	defaultSort?: string;
	/** Default sort direction. Default: 'desc' */
	defaultDir?: 'asc' | 'desc';
	/** Default view mode. Default: 'list' */
	defaultView?: string;
	/** Items per page in pages mode. Default: 20 */
	limit?: number;
	/**
	 * How one row of the list renders in a given view. When set, scroll mode
	 * sizes its batches to the viewport (see `fitToViewport`) instead of using
	 * `limit`. Pages mode keeps `limit`, so `?page=3` means the same rows to
	 * everyone who opens the link.
	 */
	scrollRows?: (view: string) => RowEstimate;
	/** Remembers the paging mode per list. Default: the route's pathname */
	listKey?: string;
}

/**
 * All common list-page state backed by URL query params.
 *
 * Replaces 5+ useState calls with a single hook. State persists across
 * navigation (back button restores search, page, sort, view, mode).
 *
 * ── PAGING MODE ──
 *
 * `mode` is 'pages' or 'scroll'. Pass `request` as the query's pagination
 * input and both work off the same `PaginationInput`:
 *
 *   · pages  — `page` is the page shown; `request` is that page.
 *   · scroll — `page` is how many pages are loaded. Scrolling asks for just
 *     the next page; anything that rebuilds the list (mount, back navigation,
 *     `reload`) asks for page 1 sized to everything loaded so far, so the rows
 *     come back in one request and scroll restoration has something to land on.
 *
 * Feed the query result to `usePagedList`, which keeps the rows and the sentinel.
 *
 * @example
 * const list = useListParams({ defaultSort: 'name', defaultDir: 'asc', defaultView: 'grid' });
 * useGetAllProductsQuery({ variables: { input: { pagination: list.request, ... } } });
 */
export function useListParams(options: UseListParamsOptions = {}) {
	const {
		defaultSort = 'createdAt',
		defaultDir = 'desc',
		defaultView = 'list',
		limit: pageLimit = 20,
		scrollRows,
	} = options;
	const { pathname } = useLocation();
	const listKey = options.listKey ?? pathname;

	const [search, setSearchParam] = useQueryParam('q', '');
	const [page, setPage] = useQueryParamInt('page', 1);
	const [view, setView] = useQueryParam('view', defaultView);
	const [sortField, setSortField] = useQueryParam('sort', defaultSort);
	const [sortDirParam, setSortDirParam] = useQueryParam('dir', defaultDir);
	// Read once: it is the default the URL param falls back to, and must not
	// change under the param while this list is mounted.
	const [storedMode] = useState(() => readPagingMode(listKey));
	const [modeParam, setModeParam] = useQueryParam('mode', storedMode);
	// The page scrolling last appended. While it matches `page` the next request
	// is that one page; once anything else moves `page` (a filter resetting it to
	// 1, a reload clearing this) the list is rebuilt from page 1.
	const [appendedPage, setAppendedPage] = useState<number | null>(null);

	const sortDirection = sortDirParam === 'asc' ? OrderTypes.Asc : OrderTypes.Desc;
	const sortDir = sortDirParam === 'asc' ? ('asc' as const) : ('desc' as const);
	const mode: PagingMode = modeParam === 'pages' ? 'pages' : 'scroll';

	// Measured once per view, not on every resize: offsets are page × batch size,
	// so the size must hold still while rows are being appended.
	const measureBatch = (v: string) => (scrollRows ? fitToViewport(scrollRows(v)) : pageLimit);
	const [batch, setBatch] = useState(() => ({ view, size: measureBatch(view) }));
	if (batch.view !== view) setBatch({ view, size: measureBatch(view) });
	const limit = mode === 'scroll' ? batch.size : pageLimit;

	const debouncedSearch = useDebouncedValue(search);

	const appending = mode === 'scroll' && page > 1 && appendedPage === page;
	const loadedPages = mode === 'scroll' && !appending ? Math.min(page, MAX_RESTORE_PAGES) : page;
	const request =
		mode === 'pages' || appending
			? { currentPage: page, limit }
			: { currentPage: 1, limit: limit * loadedPages };

	const setSearch = (value: string) => {
		setSearchParam(value);
		setPage(1);
	};

	const toggleSort = (field: string) => {
		if (sortField === field) {
			setSortDirParam(sortDirParam === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirParam('asc');
		}
		setPage(1);
	};

	// A new view can mean a new batch size, and loaded pages of the old size
	// don't line up with it — start the scroll over.
	const changeView = (next: string) => {
		setView(next);
		if (mode === 'scroll') {
			setPage(1);
			setAppendedPage(null);
		}
	};

	const setMode = (next: PagingMode) => {
		writePagingMode(listKey, next);
		setModeParam(next);
		setPage(1);
		setAppendedPage(null);
	};

	/** Scroll mode: append the next page. */
	const loadMore = () => {
		const next = loadedPages + 1;
		setAppendedPage(next);
		setPage(next);
	};

	/**
	 * Refresh after a mutation. Pages mode refetches the page shown; scroll mode
	 * rebuilds every loaded row, not just the last page fetched — otherwise rows
	 * above it stay stale.
	 */
	const reload = (refetch: () => unknown) => {
		if (appending) {
			// Changing the request to page 1 refetches on its own.
			setAppendedPage(null);
		} else {
			refetch();
		}
	};

	return {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		view,
		setView: changeView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	};
}

export type ListParams = ReturnType<typeof useListParams>;
